import * as React from 'react';

import { motion } from 'framer-motion';

import styled, { css } from 'styled-components';

import { easeTransition } from '@components/framer';

interface TooltipProps {
  className?: string;
  children?: any;
  isOpen?: boolean;
  centerPointer?: boolean;
  right?: string;
  top?: string;
  pointerBottom?: boolean;
  gradientBackground?: boolean;
  minWidth?: string;
  isDark?: boolean;
  noMotion?: boolean;
  hasShadow?: boolean;
}

const container = {
  initial: {
    y: '-100%',
    x: '-50%',
    opacity: 0,
  },
  open: {
    y: 0,
    x: '-50%',
    opacity: 1,
    transition: {
      ...easeTransition,
      duration: 0.125,
    },
  },
  closed: {
    y: '-100%',
    x: '-50%',
    opacity: 0,
  },
};

const containerRight = {
  initial: {
    y: '-100%',
    opacity: 0,
  },
  open: {
    y: 0,
    opacity: 1,
    transition: {
      ...easeTransition,
      duration: 0.125,
    },
  },
  closed: {
    y: '-100%',
    opacity: 0,
  },
};

const containerOpacity = {
  initial: {
    opacity: 0,
  },
  open: {
    opacity: 1,
    transition: {
      ...easeTransition,
      duration: 0.25,
    },
  },
  closed: {
    opacity: 0,
  },
};

export const Tooltip: React.FC<TooltipProps> = (props) => {
  return props.noMotion ? (
    <div className={`${props.className} ${props.isOpen ? 'opened' : ''}`}>
      <div className="menu-items-container">
        <div className="menu-content">{props.children}</div>
      </div>
    </div>
  ) : (
    <motion.div
      className={`${props.className} ${props.isOpen ? 'opened' : ''}`}
      initial="initial"
      animate={props.isOpen ? 'open' : 'closed'}
      exit="closed"
      variants={
        props.pointerBottom
          ? containerOpacity
          : props.right
          ? containerRight
          : container
      }
    >
      <div className="menu-items-container">
        <div className="menu-content">{props.children}</div>
      </div>
    </motion.div>
  );
};

export default styled(Tooltip)`
  position: absolute;
  top: ${(props) => (props.top ? props.top : '100%')};
  pointer-events: none;
  ${(props) =>
    props.right
      ? css`
          right: ${props.right};
        `
      : css`
          left: 50%;
          transform: translate(-50%, 0);
        `}
  ${(props) =>
    props.pointerBottom
      ? css`
          top: auto;
          bottom: 100%;
        `
      : null}


  &.opened {
    pointer-events: initial;
    z-index: ${({ theme }) => theme.zIndices.underHeader};

    &:before {
      opacity: 1;
    }
  }

  &:before {
    content: '';
    height: 30px;
    width: 30px;
    background: ${(props) =>
      props.gradientBackground
        ? props.pointerBottom
          ? 'linear-gradient(-42deg, rgba(237,10,15,1) 0%, rgba(193,82,236,1) 100%)'
          : 'linear-gradient(-42deg, rgba(193,82,236,1) 0%, rgba(237,10,15,1) 100%)'
        : props.isDark
        ? `linear-gradient(-12deg, #2A2C35 0%, #32343C 100%);`
        : css`
            ${({ theme }) => theme.colors.differentBackground}
          `};
    position: absolute;
    bottom: calc(100% - 25px - 24px);
    transform: rotate(45deg);
    border-radius: 4px 0 0 0;
    z-index: 0;
    opacity: 0;
    transition: opacity
      ${({ theme }) =>
        `${theme.transitions.duration} ${theme.transitions.type}`};

    ${(props) =>
      props.gradientBackground
        ? css`
            filter: drop-shadow(
              ${({ theme }) =>
                `${theme.general.boxShadowMenu} ${theme.colors.shadow}`}
            );
          `
        : null};

    ${(props) =>
      props.centerPointer
        ? css`
            left: 50%;
            transform: translate(-50%, 0) rotate(45deg);
          `
        : css`
            right: 25px;
          `}

    ${(props) =>
      props.pointerBottom
        ? css`
            bottom: auto;
            top: calc(100% - 25px - 24px);
            border-radius: 0 0 4px 0;
          `
        : null}

    ${(props) =>
      props.hasShadow
        ? css`
            filter: drop-shadow(
              ${({ theme }) =>
                `${theme.general.boxShadowMenu} ${theme.colors.shadow}`}
            );
          `
        : null}
  }

  .menu-items-container {
    box-sizing: border-box;
    border-radius: 10px;
    padding-top: 20px;
    width: auto;
    min-width: ${(props) => (props.minWidth ? props.minWidth : '240px')};
    ${(props) =>
      props.pointerBottom
        ? css`
            padding-top: 0;
            padding-bottom: 20px;
          `
        : null}
  }

  .menu-content {
    background: ${(props) =>
      props.gradientBackground
        ? `linear-gradient(-42deg, #da296f 0%, rgba(237,10,15,1) 100%)`
        : props.isDark
        ? `linear-gradient(-12deg, #2A2C35 0%, #32343C 100%);`
        : css`
            ${({ theme }) => theme.colors.differentBackground}
          `};
    border-radius: 10px;
    position: relative;
    padding: 20px;
    z-index: 1;

    ${(props) =>
      props.gradientBackground
        ? css`
            filter: drop-shadow(
              ${({ theme }) =>
                `${theme.general.boxShadowMenu} ${theme.colors.shadow}`}
            );
          `
        : null}
  }

  ${(props) =>
    props.hasShadow
      ? css`
          .menu-content {
            filter: drop-shadow(
              ${({ theme }) =>
                `${theme.general.boxShadowMenu} ${theme.colors.shadow}`}
            );
          }
        `
      : null}
`;
